import { Button, Grid, Group, Paper, Stack, ActionIcon, Table, Title, Loader, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import React, { useEffect, useMemo, useState } from "react";
import InputField, { InputFieldMasks, InputFieldTypes } from "../components/input_field";
import { useApp } from "../contexts/app.context";
import { DELETE_PLAN, GET_ALL_PLANS, GET_WHITELABEL_PLANS, SAVE_PLAN } from "../services/plans";
import { GET_TEMPLATES } from "../services/conversations";
import { GET_ALL_TEMPLATES } from "../services/agents";
import { useLanguage } from "../contexts/language.context";
import { currencyOptions } from "../utility/util";
import { FaTrash, FaPlus } from "react-icons/fa";

export default function PlansPage({ whitelabel = false }: { whitelabel?: boolean }) {
    const [plans, setPlans] = useState<any[]>([]);
    const [templates, setTemplates] = useState<any[]>([]);
    const [selectedPlan, setSelectedPlan] = useState<any>(null);
    const [loadingSave, setLoadingSave] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const { confirmDialog } = useApp();
    const { str } = useLanguage();

    const loadPlans = () => {
        setLoading(true);
        (whitelabel ? GET_WHITELABEL_PLANS : GET_ALL_PLANS)()
            .then(res => {
                setPlans(res);
            })
            .catch(err => {
                notifications.show({ message: err.message, color: "red" })
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const loadTemplateAgents = () => {
        GET_ALL_TEMPLATES()
            .then(agents => {
                setTemplates(agents);
            })
            .catch(err => {
                notifications.show({ message: err.message, color: 'red' })
            })
    }

    const handleSave = (res: any) => {
        let params = { ...selectedPlan, isWhitelabel: whitelabel };

        setLoadingSave(true)
        SAVE_PLAN(params)
            .then((res) => {
                loadPlans();
                setSelectedPlan(res)
                setLoadingSave(false);
            })
            .catch(err => {
                notifications.show({ message: err.message, color: "red" })
                setLoadingSave(false);
            })
    }

    const {prefix, suffix} = useMemo(() => {
        const selectedCurrency = currencyOptions.find(o => o.value === selectedPlan?.currency);
        const prefix = selectedCurrency?.prefix ?? "$";
        const suffix = selectedCurrency?.suffix ?? "";
        return { prefix, suffix };
    }, [selectedPlan?.currency]);

    const toNumber = (value: any) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    const formatCurrency = (value: number) => `${prefix}${value.toFixed(2)}${suffix}`;

    useEffect(loadPlans, []);
    useEffect(loadTemplateAgents, []);

    return <div style={{ position: 'relative' }}>
        <Grid>
            <Grid.Col span={{ base: 12, md: 12 }}>
                <Paper p="md" mb="md">
                    <Group>
                        {loading && <Loader />}
                        <InputField
                            name="pl"
                            style={{ flex: 1 }}
                            onChange={({ pl }) => {
                                pl === "new"
                                    ? setSelectedPlan({})
                                    : setSelectedPlan(plans.find(p => p.id === pl))
                            }}
                            value={selectedPlan ? selectedPlan?.id || "new" : null}
                            fieldType={InputFieldTypes.SELECT}
                            clearable={false}
                            searchable={false}
                            options={[
                                ...plans.map(p => ({ label: `${p.title} (${p.recurrence_type})`, value: p.id })),
                                { value: "new", label: str("PLAN.ADD_NEW_PLAN") }
                            ]}
                        />
                        {selectedPlan?.id && <Button color="gray" variant="outline"
                            onClick={() => {
                                confirmDialog(
                                    { text: "Do you realy wants to delete this plan?" },
                                    ({ confirmed }) => {
                                        if (confirmed) DELETE_PLAN(selectedPlan?.id)
                                            .then(() => {
                                                loadPlans();
                                                setSelectedPlan(null);
                                            })
                                            .catch(err => {
                                                notifications.show({ message: err.message })
                                            })
                                    }
                                )
                            }}
                        >{str("PLAN.DELETE")}</Button>}
                    </Group>
                </Paper>

                {selectedPlan && <>
                    <Paper p="md" mb="md">
                        <Stack>
                            <InputField
                                name="plan_type"
                                title={str("PLAN.PLAN_TYPE")}
                                value={selectedPlan?.plan_type ?? "credits"}
                                fieldType={InputFieldTypes.SELECT}
                                searchable={false}
                                clearable={false}
                                onChange={({ plan_type }) => { setSelectedPlan((sp: any) => ({ ...sp, plan_type })) }}
                                options={[
                                    { value: "credits", label: str("PLAN.CREDITS") },
                                    { value: "packages", label: str("PLAN.PACKAGES") },
                                ]}
                            />
                            <Group>
                                <InputField
                                    name="currency"
                                    title={str("PLAN.CURRENCY")}
                                    value={selectedPlan?.currency ?? "USD"}
                                    fieldType={InputFieldTypes.SELECT}
                                    searchable={true}
                                    clearable={false}
                                    style={{ flex: 1 }}
                                    onChange={({ currency }) => { setSelectedPlan((sp: any) => ({ ...sp, currency })) }}
                                    options={currencyOptions}
                                />
                                <InputField
                                    name="price"
                                    mask={InputFieldMasks.MONEY}
                                    prefix={prefix}
                                    suffix={suffix}
                                    onChange={({ price }) => setSelectedPlan((sp: any) => ({ ...sp, price }))}
                                    value={selectedPlan?.price}
                                    title={str("PLAN.PRICE")}
                                    style={{ flex: 1 }}
                                />
                                <InputField
                                    name="recurrence_type"
                                    title={str("PLAN.RECURRENCE_TYPE")}
                                    value={selectedPlan?.recurrence_type ?? "month"}
                                    fieldType={InputFieldTypes.SELECT}
                                    placeholder='Period'
                                    searchable={false}
                                    clearable={false}
                                    style={{ flex: 1 }}
                                    onChange={({ recurrence_type }) => { setSelectedPlan((sp: any) => ({ ...sp, recurrence_type })) }}
                                    options={[
                                        { value: "month", label: str("RECURRENCES.MONTH") },
                                        { value: "year", label: str("RECURRENCES.YEAR") },
                                    ]}
                                />
                            </Group>
                            {!whitelabel && <Group>
                                {
                                    selectedPlan?.plan_type === "packages"
                                        ? <InputField
                                            name="minutes"
                                            fieldType={InputFieldTypes.NUMBER}
                                            onChange={({ minutes }) => setSelectedPlan((sp: any) => ({ ...sp, minutes }))}
                                            value={selectedPlan?.minutes}
                                            title={str("PLAN.INCLUDED_MINUTES")}
                                            style={{ flex: 1 }}
                                        />
                                        : <InputField
                                            name="add_on_calls"
                                            mask={InputFieldMasks.MONEY}
                                            onChange={({ add_on_calls }) => setSelectedPlan((sp: any) => ({ ...sp, add_on_calls }))}
                                            value={selectedPlan?.add_on_calls}
                                            title={str("PLAN.ADD_ON_CALLS")}
                                            style={{ flex: 1 }}
                                        />
                                }
                                <InputField
                                    name="max_agents"
                                    fieldType={InputFieldTypes.NUMBER}
                                    onChange={({ max_agents }) => setSelectedPlan((sp: any) => ({ ...sp, max_agents }))}
                                    value={selectedPlan?.max_agents}
                                    title={str("PLAN.MAX_AGENTS")}
                                    style={{ flex: 1 }}
                                />
                                <InputField
                                    name="max_users"
                                    fieldType={InputFieldTypes.NUMBER}
                                    onChange={({ max_users }) => setSelectedPlan((sp: any) => ({ ...sp, max_users }))}
                                    value={selectedPlan?.max_users}
                                    title={str("PLAN.MAX_USERS")}
                                    style={{ flex: 1 }}
                                />
                            </Group>}
                            {!whitelabel && <Group>
                                <InputField
                                    name="phone_connect_price"
                                    mask={InputFieldMasks.MONEY}
                                    prefix={prefix}
                                    suffix={suffix}
                                    onChange={({ phone_connect_price }) => setSelectedPlan((sp: any) => ({ ...sp, phone_connect_price }))}
                                    value={selectedPlan?.phone_connect_price}
                                    title={str("PLAN.BOUGHT_PHONE_MONTHLY_PRICE")}
                                    style={{ flex: 1 }}
                                />
                                <InputField
                                    name="phone_host_price"
                                    mask={InputFieldMasks.MONEY}
                                    prefix={prefix}
                                    suffix={suffix}
                                    onChange={({ phone_host_price }) => setSelectedPlan((sp: any) => ({ ...sp, phone_host_price }))}
                                    value={selectedPlan?.phone_host_price}
                                    title={str("PLAN.CONNECTED_PHONE_MONTHLY_PRICE")}
                                    style={{ flex: 1 }}
                                />
                                <InputField
                                    name="knowledge_price"
                                    mask={InputFieldMasks.MONEY}
                                    prefix={prefix}
                                    suffix={suffix}
                                    onChange={({ knowledge_price }) => setSelectedPlan((sp: any) => ({ ...sp, knowledge_price }))}
                                    value={selectedPlan?.knowledge_price}
                                    title={str("PLAN.KNOWLEDGE_FILE_MONTHLY_PRICE")}
                                    style={{ flex: 1 }}
                                />
                            </Group>}
                            {!whitelabel && <>
                                <Group>
                                    {selectedPlan?.plan_type === "packages"
                                        ? <InputField
                                            name="trial_minutes"
                                            fieldType={InputFieldTypes.NUMBER}
                                            onChange={({ trial_minutes }) => setSelectedPlan((sp: any) => ({ ...sp, trial_minutes }))}
                                            value={selectedPlan?.trial_minutes ?? 10}
                                            title={str("PLAN.MINUTES_TRIAL")}
                                            style={{ flex: 1 }}
                                        />
                                        : <InputField
                                            name="free_credits"
                                            mask={InputFieldMasks.MONEY}
                                            onChange={({ free_credits }) => setSelectedPlan((sp: any) => ({ ...sp, free_credits }))}
                                            value={selectedPlan?.free_credits ?? 10}
                                            title={str("PLAN.FREE_CREDITS")}
                                            style={{ flex: 1 }}
                                        />}
                                    <InputField
                                        name="trial_period"
                                        fieldType={InputFieldTypes.NUMBER}
                                        onChange={({ trial_period }) => setSelectedPlan((sp: any) => ({ ...sp, trial_period }))}
                                        value={selectedPlan?.trial_period ?? 15}
                                        title={str("PLAN.TRIAL_PERIOD")}
                                        style={{ flex: 1 }}
                                    />
                                    <InputField
                                        name="payment_required"
                                        fieldType={InputFieldTypes.CHECKBOX}
                                        onChange={({ payment_required }) => setSelectedPlan((sp: any) => ({ ...sp, payment_required }))}
                                        value={selectedPlan?.payment_required ?? true}
                                        title={str("PLAN.REQUIRES_CARD_ON_SIGNUP")}
                                        style={{ flex: 1 }}
                                    />
                                </Group>
                                <Group>
                                    <InputField
                                        name="templates"
                                        fieldType={InputFieldTypes.SELECT}
                                        onChange={({ templates }) => setSelectedPlan((sp: any) => ({ ...sp, templates }))}
                                        value={(selectedPlan?.templates ?? []).map(t => t.id ?? t)}
                                        options={templates.map(t => ({ label: t.title, value: t.id ?? t._id }))}
                                        title={str("PLAN.TEMPLATES")}
                                        multiple
                                        style={{ flex: 1 }}
                                    />
                                </Group>
                            </>}
                            {!whitelabel && selectedPlan?.plan_type === "packages" && <Paper p="md" mt="md" style={{ border: '1px solid #DFDFDF' }}>
                                <Group justify="space-between" mb="md">
                                    <Title order={5}>Add-on Pricing Tiers</Title>
                                    <Button
                                        size="xs"
                                        leftSection={<FaPlus />}
                                        onClick={() => {
                                            const newAddOns = [...(selectedPlan?.add_ons ?? [])];
                                            newAddOns.push({ minMinutes: 0, maxMinutes: 0, pricePerMinute: 0 });
                                            setSelectedPlan((sp: any) => ({ ...sp, add_ons: newAddOns }));
                                        }}
                                    >
                                        Add Tier
                                    </Button>
                                </Group>
                                {(selectedPlan?.add_ons ?? []).length > 0 ? (
                                    <Table>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Min Minutes</Table.Th>
                                                <Table.Th>Max Minutes</Table.Th>
                                                <Table.Th>Price Per Minute</Table.Th>
                                                <Table.Th>Range Preview</Table.Th>
                                                <Table.Th ta="right">Actions</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {(selectedPlan?.add_ons ?? []).map((addon: any, idx: number) => {
                                                const minMinutes = toNumber(addon.minMinutes);
                                                const maxMinutes = toNumber(addon.maxMinutes);
                                                const pricePerMinute = toNumber(addon.pricePerMinute);
                                                const minPrice = minMinutes * pricePerMinute;
                                                const maxPrice = maxMinutes * pricePerMinute;
                                                const invalidRange = maxMinutes < minMinutes;

                                                return <Table.Tr key={idx}>
                                                    <Table.Td>
                                                        <InputField
                                                            name={`min_${idx}`}
                                                            fieldType={InputFieldTypes.NUMBER}
                                                            value={addon.minMinutes}
                                                            onChange={(val: any) => {
                                                                const newAddOns = [...(selectedPlan?.add_ons ?? [])];
                                                                newAddOns[idx] = { ...newAddOns[idx], minMinutes: val[`min_${idx}`] };
                                                                setSelectedPlan((sp: any) => ({ ...sp, add_ons: newAddOns }));
                                                            }}
                                                            size="xs"  
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <InputField
                                                            name={`max_${idx}`}
                                                            fieldType={InputFieldTypes.NUMBER}
                                                            value={addon.maxMinutes}
                                                            onChange={(val: any) => {
                                                                const newAddOns = [...(selectedPlan?.add_ons ?? [])];
                                                                newAddOns[idx] = { ...newAddOns[idx], maxMinutes: val[`max_${idx}`] };
                                                                setSelectedPlan((sp: any) => ({ ...sp, add_ons: newAddOns }));
                                                            }}
                                                            size="xs"
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <InputField
                                                            name={`price_${idx}`}
                                                            mask={InputFieldMasks.MONEY}
                                                            prefix={prefix}
                                                            suffix={suffix}
                                                            value={addon.pricePerMinute}
                                                            onChange={(val: any) => {
                                                                const newAddOns = [...(selectedPlan?.add_ons ?? [])];
                                                                newAddOns[idx] = { ...newAddOns[idx], pricePerMinute: val[`price_${idx}`] };
                                                                setSelectedPlan((sp: any) => ({ ...sp, add_ons: newAddOns }));
                                                            }}
                                                            size="xs"
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Text size="xs" fw={500}>{`${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`}</Text>
                                                        <Text size="xs" c="dimmed">{`${minMinutes}m to ${maxMinutes}m @ ${formatCurrency(pricePerMinute)}/min`}</Text>
                                                        {invalidRange && <Text size="xs" c="red">Max must be greater than Min</Text>}
                                                    </Table.Td>
                                                    <Table.Td ta="right">
                                                        <ActionIcon
                                                            size="sm"
                                                            color="red"
                                                            onClick={() => {
                                                                const newAddOns = (selectedPlan?.add_ons ?? []).filter((_: any, i: number) => i !== idx);
                                                                setSelectedPlan((sp: any) => ({ ...sp, add_ons: newAddOns }));
                                                            }}
                                                        >
                                                            <FaTrash />
                                                        </ActionIcon>
                                                    </Table.Td>
                                                </Table.Tr>
                                            })}
                                        </Table.Tbody>
                                    </Table>
                                ) : (
                                    <div style={{ textAlign: 'center', color: 'gray', padding: '20px' }}>
                                        No pricing tiers defined. Click "Add Tier" to create one.
                                    </div>
                                )}
                            </Paper>}
                            {/* <InputField
                            name="exceed_price"
                            mt="md"
                            mask={InputFieldMasks.MONEY}
                            prefix={prefix}
                            suffix={suffix}
                            onChange={({exceed_price}) => setSelectedPlan((sp: any) => ({...sp, exceed_price}))}
                            value={selectedPlan?.exceed_price}
                            title="Exceed Price"
                        /> */}
                        </Stack>
                    </Paper>
                    <Paper p="md">
                        <InputField
                            name="title"
                            mt="md"
                            onChange={({ title }) => setSelectedPlan((sp: any) => ({ ...sp, title }))}
                            value={selectedPlan?.title}
                            title={str("PLAN.PLAN_TITLE")}
                        />
                        <InputField
                            name="description"
                            mt="md"
                            fieldType={InputFieldTypes.TEXTAREA}
                            onChange={({ description }) => setSelectedPlan((sp: any) => ({ ...sp, description }))}
                            value={selectedPlan?.description}
                            title={str("PLAN.PLAN_DESCRIPTION")}
                        />
                    </Paper>
                    <Button mt="md" loading={loadingSave} onClick={handleSave}>{str("SAVE")}</Button>
                </>}
            </Grid.Col>
        </Grid>
    </div>
}
